'use client';

import CustomButton from '@/components/custom-button';
import CustomInput from '@/components/custom-input';
import CustomForm, { FormItemProps } from '@/components/custom-RHF-form';
import { template_error } from '@/components/custom-toast/sonner-with-template';
import { pageDirectories } from '@/constant/route';
import { cn } from '@/lib/utils';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import { ChangePasswordRequest } from '@/server/api/v1/user/type';
import { changeAcccountPassword } from '@/server/api/v1/user/user.action';
import { api } from '@/trpc/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, LockKeyhole } from 'lucide-react';
import { performSignOut } from '@/lib/perform-sign-out';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import CustomToast from '../custom-toast';
import { RePassword, RePasswordSchema } from './schema';

const defaultValues: RePassword = {
    old: '',
    new: '',
    confirm_new: '',
};

/**sử dụng cho form đổi mật khẩu bằng token */
const defaultValuesWithToken: RePassword = {
    old: '_token_',
    new: '',
    confirm_new: '',
};

type CustomRePasswordProps = {
    className?: string;

    /**
     * Nếu có truyền token
     * Sử dụng token để đặt lại mật khẩu
     */
    token?: string;
};

const CustomRePassword = ({ className, token }: CustomRePasswordProps) => {
    const transDef = useTranslations('System');
    const transRePwd = useTranslations('RePassword');

    const apiUtils = api.useUtils();
    const authData = useAuthenticationStore((x) => x.auth);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSetPwdWithToken, setIsSetPwdWithToken] = useState(!!token);

    const methods = useForm<RePassword>({
        mode: 'onBlur',
        defaultValues: !!token ? defaultValuesWithToken : defaultValues,
        delayError: 500,
        resolver: zodResolver(RePasswordSchema(transDef)),
    });
    const onReset = useCallback(
        async (msg?: string) => {
            methods.reset(defaultValues);
            if (msg) {
                CustomToast.sonnerV2({
                    type: 'success',
                    title: msg,
                    desc: 'Vui lòng đăng nhập lại',
                });

                setTimeout(async () => {
                    const result = await performSignOut({
                        redirect: false,
                    });

                    // Full navigation (không router.push): /api/signout cần browser
                    // navigation thật để Set-Cookie xóa session có hiệu lực.
                    window.location.replace(
                        result.url || pageDirectories['Signin']
                    );
                }, 2500);
            }
        },
        [methods]
    );

    const onChangePassword = useCallback(
        async (data: RePassword) => {
            setIsSubmitting(true);

            // Nếu có truyền token thì gọi api đổi mật khẩu bằng token
            if (!!token) {
                // TODO: Chưa làm
            } else if (authData && authData.UserName) {
                const sendData: ChangePasswordRequest = {
                    userName: authData.UserName,
                    oldPassword: data.old,
                    newPassword: data.new,
                    rePassword: data.confirm_new,
                };
                const result = await changeAcccountPassword(sendData);

                if (result?.data?.isSuccessStatus) {
                    onReset(transRePwd('ChangePasswordSuccess'));
                } else {
                    console.error(
                        `Error when change password ->`,
                        result?.data
                    );

                    CustomToast.sonner.error(
                        template_error({
                            title: transDef('System_AnErrorHasOccurred'),
                            desc: result?.data?.errors,
                        })
                    );
                }
            }

            setIsSubmitting(false);
        },
        [authData, onReset, token, transDef, transRePwd]
    );

    const rls = useMemo<FormItemProps<RePassword>>(() => {
        const subRls: FormItemProps<RePassword> = [
            {
                type: 'Item',
                name: 'old',
                label: transRePwd('OldPassword'),
                wrapperClassName: 'col-span-full',
                required: true,
                children: (
                    <CustomInput.Prefix
                        prefix={<LockKeyhole size={16} />}
                        isInputPassword
                        placeholder={transDef('System_Enter') + '...'}
                        disabled={isSubmitting}
                        className='no-ring'
                    />
                ),
            },
            {
                type: 'Item',
                name: 'new',
                label: transRePwd('NewPassword'),
                required: true,
                wrapperClassName: 'col-span-full',
                children: (
                    <CustomInput.Prefix
                        prefix={<LockKeyhole size={16} />}
                        isInputPassword
                        placeholder={transDef('System_Enter') + '...'}
                        autoComplete='new-password'
                        disabled={isSubmitting}
                        className='no-ring'
                    />
                ),
            },
            {
                type: 'Item',
                name: 'confirm_new',
                label: transRePwd('ConfirmPassword'),
                required: true,
                wrapperClassName: 'col-span-full',
                children: (
                    <CustomInput.Prefix
                        prefix={<LockKeyhole size={16} />}
                        isInputPassword
                        placeholder={transDef('System_Enter') + '...'}
                        autoComplete='new-password'
                        disabled={isSubmitting}
                        className='no-ring'
                    />
                ),
            },
        ];

        /**Remove first item if set pwd with token */
        if (isSetPwdWithToken) {
            subRls.shift();
        }

        return [
            {
                type: 'Grid',
                wrapperClassName: 'gap-y-2',
                children: subRls,
            },
        ];
    }, [isSetPwdWithToken, isSubmitting, transDef, transRePwd]);

    return (
        <CustomForm.FormProvider {...methods}>
            <div className={cn('max-w-xl px-2 md:px-3', className)}>
                <CustomForm.Form rls={rls} useFormTag>
                    <div className='flex items-center gap-x-2 pt-2'>
                        <CustomButton.Root
                            className={cn('min-w-32 max-md:flex-1')}
                            icon={<KeyRound size={16} />}
                            onClick={methods.handleSubmit(
                                onChangePassword,
                                (err) => {
                                    console.error(
                                        `change-password-form-failed`,
                                        err
                                    );
                                }
                            )}
                            isLoading={isSubmitting}
                        >
                            {transRePwd('ChangePassword')}
                        </CustomButton.Root>
                    </div>
                </CustomForm.Form>
            </div>
        </CustomForm.FormProvider>
    );
};

export default memo(CustomRePassword);
